import React from "react";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import Section from "../ui/Section";
import Reveal from "../ui/Reveal";
import { Accent, Display, Label } from "../ui/Type";
import { LOCATIONS } from "../../data/locations";
import { ALSO_SERVED } from "../../data/company";
import { color, font, motion, type } from "../../design/tokens";

/**
 * Service areas.
 *
 * Four large rule-separated rows rather than a map or a list of pins. A map
 * needs an API key, ships a lot of JavaScript, and tells a homeowner nothing
 * they didn't already know about where their own house is; four links to four
 * pages with genuinely different content is more use to them and to search.
 *
 * `exclude` drops the current city when this renders on a city page, so a page
 * never links to itself.
 */
const ServiceAreasSection = ({ exclude, tone = "paper", title, eyebrow = "Where we work" }) => {
  const locations = LOCATIONS.filter((location) => location.slug !== exclude);

  return (
    <Section tone={tone} id="service-areas">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 3, md: 8 },
          alignItems: "end",
          marginBottom: { xs: 5, md: 8 },
        }}
      >
        <Box>
          <Label>{eyebrow}</Label>
          <Display as="h2" size="lg" sx={{ marginTop: 3 }}>
            {title || (
              <>
                Based in Pickering. <Accent>Working across Durham</Accent>.
              </>
            )}
          </Display>
        </Box>
        <Box component="p" sx={{ ...type.body, color: color.graphite, margin: 0, maxWidth: 460 }}>
          Each of these has its own page, because renovating a 1970s home in
          west Pickering and finishing a new-build basement in Brooklin are not
          the same job. We also work in{" "}
          {ALSO_SERVED.slice(0, -1).join(", ")} and {ALSO_SERVED.slice(-1)}.
        </Box>
      </Box>

      <Box sx={{ borderTop: `1px solid ${color.line}` }}>
        {locations.map((location, index) => (
          <Reveal key={location.slug} delay={index * 60}>
            <Box
              component={Link}
              to={`/${location.slug}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "0.4fr 1fr auto" },
                columnGap: { md: 6 },
                rowGap: 1.5,
                alignItems: "center",
                paddingBlock: { xs: 3.5, md: 4.5 },
                borderBottom: `1px solid ${color.line}`,
                "&:hover .flr-area-name": { color: color.clay, transform: "translateX(8px)" },
                "&:hover .flr-area-arrow": { opacity: 1, transform: "translateX(0)" },
              }}
            >
              <Box
                className="flr-area-name"
                component="span"
                sx={{
                  fontFamily: font.display,
                  fontSize: { xs: "1.9rem", md: "2.5rem" },
                  lineHeight: 1.05,
                  color: color.ink,
                  transition: `color ${motion.base} ${motion.ease}, transform ${motion.base} ${motion.ease}`,
                }}
              >
                {location.name}
              </Box>

              <Box
                component="span"
                sx={{ ...type.bodySm, color: color.graphite, maxWidth: 620 }}
              >
                {location.lede}
              </Box>

              <Box
                className="flr-area-arrow"
                component="svg"
                aria-hidden="true"
                viewBox="0 0 16 16"
                sx={{
                  width: 16,
                  height: 16,
                  fill: "none",
                  stroke: color.clay,
                  strokeWidth: 1.5,
                  opacity: { xs: 0.4, md: 0 },
                  transform: { md: "translateX(-8px)" },
                  transition: `opacity ${motion.base} ${motion.ease}, transform ${motion.base} ${motion.ease}`,
                  justifySelf: { xs: "start", md: "end" },
                }}
              >
                <path d="M1 8h13M9 3l5 5-5 5" />
              </Box>
            </Box>
          </Reveal>
        ))}
      </Box>
    </Section>
  );
};

export default ServiceAreasSection;
